// D-265 sec.2-3: the archive window of an engagement (pure model, unit-tested).

export interface EngagementArchivePreview {
  engagement_id: string;
  name: string | null;
  type: string;
  organization_id: string;
  organization_name: string | null;
  archived: boolean;
  archived_at: string | null;
  counts: {
    tasks: number;
    open_tasks: number;
    tickets: number;
    open_tickets: number;
    discussions: number;
    milestones: number;
    projects: number;
    work_logs: number;
    hours: number;
    open_timers: number;
  };
  open_timers: Array<{ user_name: string | null; ticket_title: string | null; status: string }>;
  billed: boolean;
  billed_count: number;
  can_purge: boolean;
  can_restore: boolean;
}

/** What goes to the archive, in display order; only what exists (count > 0). */
export type ArchiveItemKey = 'tasks' | 'tickets' | 'discussions' | 'milestones' | 'projects' | 'work_logs' | 'open_timers';

export function archiveItems(p: EngagementArchivePreview): Array<{ key: ArchiveItemKey; count: number; open?: number }> {
  const c = p.counts;
  const all: Array<{ key: ArchiveItemKey; count: number; open?: number }> = [
    { key: 'tasks', count: c.tasks, open: c.open_tasks },
    { key: 'tickets', count: c.tickets, open: c.open_tickets },
    { key: 'discussions', count: c.discussions },
    { key: 'milestones', count: c.milestones },
    { key: 'projects', count: c.projects },
    { key: 'work_logs', count: c.work_logs },
    { key: 'open_timers', count: c.open_timers },
  ];
  return all.filter((i) => i.count > 0);
}

/** The permanent-delete option: hidden (not an OTE manager), blocked (a charge was produced, Q-212) or available. */
export type PurgeState = 'hidden' | 'blocked_billed' | 'available';

export function purgeState(p: Pick<EngagementArchivePreview, 'can_purge' | 'billed'>): PurgeState {
  if (!p.can_purge) return 'hidden';
  return p.billed ? 'blocked_billed' : 'available';
}

/**
 * QX-1273 - nothing at all under the engagement: the window offers the regular
 * delete (D-265 sec.1, waits for the server) instead of the permanent one. OTE manager only.
 */
export function plainDeleteAvailable(p: Pick<EngagementArchivePreview, 'can_purge' | 'counts'>): boolean {
  return p.can_purge && archiveItems(p as EngagementArchivePreview).length === 0;
}

/** The archive button: not for an engagement that is already in the archive. */
export function canArchive(p: Pick<EngagementArchivePreview, 'archived'>): boolean {
  return !p.archived;
}

/**
 * FIX-SMALL30 (D-265): the ⋯ entry that opens the archive window. Outside the archive it is
 * "ארכיון / מחיקה"; for an engagement that is already in the archive there is nothing to archive,
 * so it reads "מחיקה לצמיתות" (the window then offers the permanent delete only).
 */
export function archiveEntryLabelKey(inArchive: boolean): 'engagements.archiveOrDelete' | 'engagements.deletePermanently' {
  return inArchive ? 'engagements.deletePermanently' : 'engagements.archiveOrDelete';
}

/** D-166 sec.11 + Q-215 - the undo after an archive is offered only to who may restore (the OTE manager). */
export function offerUndoAfterArchive(kind: 'archived' | 'purged' | 'deleted', canRestore: boolean): boolean {
  return kind === 'archived' && canRestore;
}

/** The typed name confirms the delete: exact (trimmed) match. */
export function nameMatches(typed: string, name: string | null | undefined): boolean {
  const n = String(name ?? '').trim();
  return n.length > 0 && typed.trim() === n;
}

/** The window's steps: overview -> (purge) type the name -> "are you sure?". */
export type ArchiveStep = 'overview' | 'purge_name' | 'purge_confirm';

export function nextStep(step: ArchiveStep, ctx: { typed: string; name: string | null | undefined; purge: PurgeState }): ArchiveStep {
  if (ctx.purge !== 'available') return 'overview';
  if (step === 'overview') return 'purge_name';
  if (step === 'purge_name') return nameMatches(ctx.typed, ctx.name) ? 'purge_confirm' : 'purge_name';
  return step;
}
